import React, { useEffect, useState } from 'react';
import { Alert, Descriptions, Drawer, Spin, Table, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { getVisitResult } from '../../services/api';
import { getApiError } from '../../utils/apiError';

const { Title } = Typography;

const BAND_COLOUR: { [b: string]: string } = { GREEN: 'green', YELLOW: 'gold', RED: 'red' };
const KIND_LABEL: { [k: string]: string } = { DOMAIN: 'Domain', SUB_DOMAIN: 'Sub-domain', PROJECT: 'Project' };

export const show = (value: string | null | undefined) =>
  value === null || value === undefined ? '-' : (Math.round(parseFloat(value) * 100 + 1e-9) / 100).toFixed(2);

interface Props { visitId: string; open: boolean; onClose: () => void; }

const ResultPanel: React.FC<Props> = ({ visitId, open, onClose }) => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setData(null);
    setError('');
    getVisitResult(visitId).then((res) => setData(res.data))
      .catch((err) => setError(getApiError(err, 'Could not load the result.')));
  }, [open, visitId]);

  const scoreRows = (data?.scores || []).filter((s: any) => ['DOMAIN', 'SUB_DOMAIN'].includes(s.kind));

  return (
    <Drawer title="Rating result" open={open} onClose={onClose} size={820} destroyOnHidden>
      {error && <Alert type="error" showIcon message={error} />}
      {!data && !error && <Spin />}
      {data && data.status !== 'COMPLETED' && (
        <Alert type="info" showIcon message="The official rating is calculated when the reviewer completes the visit." />
      )}
      {data && data.status === 'COMPLETED' && (
        <>
          <Descriptions bordered size="small" column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="Final rating">
              <Title level={3} style={{ margin: 0 }}>
                {show(data.final_score)} <Tag color={BAND_COLOUR[data.band]}>{data.band}</Tag>
              </Title>
            </Descriptions.Item>
            <Descriptions.Item label="This audit">{show(data.current_score)}</Descriptions.Item>
            <Descriptions.Item label="Past NCs closed">
              {data.past_score === null ? 'No earlier audit' : show(data.past_score)}
            </Descriptions.Item>
          </Descriptions>

          <Title level={5}>Scores</Title>
          <Table
            size="small" rowKey="code" pagination={false} dataSource={scoreRows}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 110 },
              { title: 'Level', dataIndex: 'kind', width: 110, render: (k: string) => KIND_LABEL[k] || k },
              { title: 'Name', dataIndex: 'name' },
              {
                title: 'Score', dataIndex: 'score', width: 150,
                render: (v: string, r: any) => r.not_applicable ? <Tag>Closed</Tag> : (
                  <>{show(v)} {r.band && <Tag color={BAND_COLOUR[r.band]}>{r.band}</Tag>}</>
                ),
              },
            ]}
          />

          <Title level={5} style={{ marginTop: 24 }}>Closed areas</Title>
          <Table
            size="small" rowKey="code" pagination={false} dataSource={data.closed_areas}
            locale={{ emptyText: 'None' }}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 110 },
              { title: 'Name', dataIndex: 'name' },
              { title: 'Reason', dataIndex: 'reason' },
              { title: 'Remarks', dataIndex: 'remarks' },
            ]}
          />

          <Title level={5} style={{ marginTop: 24 }}>Changes made by the reviewer</Title>
          <Table
            size="small" rowKey={(r: any) => `${r.question}-${r.at}`} pagination={false}
            dataSource={data.reviewer_changes} locale={{ emptyText: 'None' }}
            columns={[
              { title: 'Question', dataIndex: 'question', width: 100 },
              { title: 'Change', render: (_: any, r: any) => `${r.from}${r.old_level ? ` (${r.old_level})` : ''} → ${r.to}${r.new_level ? ` (${r.new_level})` : ''}` },
              { title: 'Reason', dataIndex: 'reason' },
              { title: 'By', dataIndex: 'by', width: 130 },
              { title: 'On', dataIndex: 'at', width: 120, render: (v: string) => dayjs(v).format('DD MMM YYYY') },
            ]}
          />
        </>
      )}
    </Drawer>
  );
};

export default ResultPanel;
